import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {CameraCredentials} from '@/entities/stream/types';
import {Banner} from '@/shared/ui/banner';
import {fonts} from '@/shared/ui/fonts';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The two values an owner pastes into the camera's web UI («Сеть → ONVIF → P2P»).
 *
 * Selectable text rather than a copy button: the app deliberately carries no
 * clipboard dependency (see InviteSheet), and a long press selects on every
 * platform. The secret is shown ONCE — only its hash is kept — so the card says so.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  field: {gap: spacing(1)},
  value: {
    fontFamily: fonts.mono.medium,
    fontSize: 13,
    padding: spacing(3),
    borderRadius: radii.md,
    borderWidth: 1,
  },
});

export function CameraCredentialsCard({credentials}: {credentials: CameraCredentials}) {
  const {t} = useTranslation();
  const p = useTheme();
  return (
    <View style={styles.root}>
      <AppText variant="body" color={p.mutedForeground}>
        {t('camera.add.pasteHint')}
      </AppText>
      <View style={styles.field}>
        <AppText variant="label">{t('camera.add.signalingUrl')}</AppText>
        <AppText selectable style={[styles.value, {borderColor: p.border}]}>
          {credentials.signalingUrl}
        </AppText>
      </View>
      <View style={styles.field}>
        <AppText variant="label">{t('camera.add.secret')}</AppText>
        <AppText
          selectable
          style={[styles.value, {borderColor: p.primary, backgroundColor: p.accent}]}
          testID="camera-secret">
          {credentials.secret}
        </AppText>
      </View>
      <Banner tone="warning" title={t('camera.add.shownOnce')} />
    </View>
  );
}
